import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FaSearch, FaGift, FaTimes, FaMinus, FaPlus } from 'react-icons/fa';
import { getMyGreetingCards, sendGift } from '../../Services/User/GiftStore';
import { showToast } from '../Toast/toast';
import CustomButton from '../CustomButton';
import './GreetingCardsModal.css';

const PAGE_LIMIT = 12;

const GreetingCardsModal = ({ show, onHide, userId, onSent }) => {
  const [greetingCards, setGreetingCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [paginationMeta, setPaginationMeta] = useState(null);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (show) {
      setSearchQuery('');
      setPage(1);
      setGreetingCards([]);
      fetchGreetingCards(1, '', true);
    }
  }, [show]);

  // Debounced search effect
  useEffect(() => {
    if (!show) return;

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setGreetingCards([]);
      fetchGreetingCards(1, searchQuery, true);
    }, 500); // 500ms debounce

    // Cleanup timeout on unmount or when searchQuery changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, show]);

  const fetchGreetingCards = async (pageNum = 1, search = '', reset = false) => {
    try {
      setLoading(true);
      const response = await getMyGreetingCards(pageNum, PAGE_LIMIT, search.trim());

      if (response.status && response.data) {
        const cards = Array.isArray(response.data) ? response.data : [];

        if (reset) {
          setGreetingCards(cards);
          setPage(pageNum);
        } else {
          setGreetingCards(prev => [...prev, ...cards]);
          setPage(pageNum);
        }

        // Update pagination meta
        if (response.meta) {
          setPaginationMeta(response.meta);
          setHasMore(response.meta.current_page < response.meta.last_page);
        } else {
          setHasMore(cards.length > 0);
        }

        // Initialize quantities
        const initialQuantities = {};
        cards.forEach(card => {
          const cardId = card.gift?.id || card.id;
          if (!quantities[cardId]) {
            initialQuantities[cardId] = 1;
          }
        });
        setQuantities(prev => ({ ...prev, ...initialQuantities }));
      }
    } catch (error) {
      console.error('Error fetching greeting cards:', error);
      showToast('Failed to load greeting cards', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (cardId, change) => {
    setQuantities(prev => {
      const currentQty = prev[cardId] || 1;
      const newQty = currentQty + change;
      return {
        ...prev,
        [cardId]: newQty < 1 ? 1 : newQty
      };
    });
  };

  const handleSendCard = async (card) => {
    if (!userId) {
      showToast('User ID is required', 'error');
      return;
    }

    try {
      const cardId = card.gift?.id || card.id;
      setSending(prev => ({ ...prev, [cardId]: true }));
      const quantity = quantities[cardId] || 1;

      const response = await sendGift(cardId, quantity, userId);

      if (response.status) {
        await onSent?.({
          card,
          quantity,
          response
        });
        showToast('Greeting card sent successfully!', 'success');
        // Reset quantity after sending
        setQuantities(prev => ({ ...prev, [cardId]: 1 }));
        onHide?.();
      } else {
        showToast(response.message || 'Failed to send card', 'error');
      }
    } catch (error) {
      console.error('Error sending greeting card:', error);
      showToast(error.message || 'Failed to send greeting card', 'error');
    } finally {
      const cardId = card.gift?.id || card.id;
      setSending(prev => ({ ...prev, [cardId]: false }));
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    fetchGreetingCards(nextPage, searchQuery, false);
  };

  const filteredCards = greetingCards.filter(card => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) return true;

    const gift = card.gift || card;
    const searchableText = [
      gift.title,
      gift.description,
      gift.card_name,
      gift.gift_title,
      card.title,
      card.description,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedSearch);
  });

  return (
    <Modal show={show} onHide={onHide} centered className="greeting-cards-modal" size="lg">
      <Modal.Header className="greeting-cards-modal-header">
        <Modal.Title>Select Greeting Card / Gifts</Modal.Title>
        <button className="modal-close-btn" onClick={onHide}>
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body className="greeting-cards-modal-body">
        {/* Search Bar */}
        <div className="greeting-cards-search">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search...."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Greeting Cards List */}
        <div className="greeting-cards-list">
          {loading && greetingCards.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">Loading greeting cards...</p>
            </div>
          ) : filteredCards.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No greeting cards found</p>
            </div>
          ) : (
            filteredCards.map((card) => {
              const gift = card.gift || card;
              const cardId = gift.id || card.id;
              const cardTitle = gift.title || 'Title ABC';
              const cardDescription = gift.description || 'Lorem Ipsum Dolor Sit Amet, Consectetur Adipiscing Elit. Aenean Euismod Bibendum Laoreet. Proin Gravida Dolor Sit Amet Lacus Accumsan Et Viverra Justo Commodo.';
              const cardImage = gift.image?.path || (gift.image ? `${imageBaseUrl}${gift.image}` : null) || 'https://via.placeholder.com/150';

              return (
                <div key={card.id} className="greeting-card-item">
                  <div className="card-image-wrapper">
                    <img
                      src={cardImage}
                      alt={cardTitle}
                      className="card-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150';
                      }}
                    />
                  </div>

                  <div className="card-content">
                    <h6 className="card-title">{cardTitle}</h6>

                    <div className="card-actions">
                      <div className="quantity-selector">
                        <button
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(cardId, -1)}
                          disabled={quantities[cardId] <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span className="quantity-value">{quantities[cardId] || 1}</span>
                        <button
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(cardId, 1)}
                        >
                          <FaPlus />
                        </button>
                      </div>

                      <button
                        className="send-card-btn"
                        onClick={() => handleSendCard(card)}
                        disabled={sending[cardId]}
                      >
                        <FaGift className="gift-icon" />
                        <span>Send Card</span>
                      </button>
                    </div>

                    <p className="card-description">
                      {cardDescription}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Load More Button */}
        {hasMore && !loading && greetingCards.length > 0 && (
          <div className="text-center mt-4">
            <CustomButton
              onClick={handleLoadMore}
              className="load-more-btn"
            >
              Load More
            </CustomButton>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GreetingCardsModal;

